import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL, fmtDuration } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Users, Film, Scissors, Layers, Clock, Loader2, ArrowRight, Play, ThumbsUp, ThumbsDown, X } from "lucide-react";
import { toast } from "sonner";

function initialsFor(nameOrEmail) {
  if (!nameOrEmail) return "?";
  const trimmed = nameOrEmail.split("@")[0];
  const parts = trimmed.replace(/[._-]/g, " ").split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return iso.slice(0, 10);
}

export default function Team() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingWs, setLoadingWs] = useState(false);
  const [previewClip, setPreviewClip] = useState(null);

  useEffect(() => {
    api.get("/team/admins")
      .then((r) => {
        setAdmins(r.data.admins || []);
        // Default: select the current user, else the first admin
        const me = (r.data.admins || []).find((a) => a.id === user?.id);
        const first = (r.data.admins || [])[0];
        setSelectedId(me?.id || first?.id || null);
      })
      .catch(() => toast.error("Failed to load team"))
      .finally(() => setLoadingList(false));
  }, [user?.id]);

  const refreshWorkspace = () => {
    if (!selectedId) return;
    api.get(`/team/admins/${selectedId}/workspace`)
      .then((r) => setWorkspace(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (!selectedId) return;
    setLoadingWs(true);
    api.get(`/team/admins/${selectedId}/workspace`)
      .then((r) => setWorkspace(r.data))
      .catch(() => toast.error("Failed to load workspace"))
      .finally(() => setLoadingWs(false));
  }, [selectedId]);

  const handleFeedback = async (clip, feedback) => {
    const newFb = clip.feedback === feedback ? null : feedback;
    // Optimistic update
    setWorkspace((ws) => ws && {
      ...ws,
      clips: ws.clips.map((c) => c.id === clip.id ? { ...c, feedback: newFb } : c),
    });
    setPreviewClip((p) => (p && p.id === clip.id ? { ...p, feedback: newFb } : p));
    try {
      await api.put(`/clips/${clip.id}/feedback`, { feedback: newFb });
    } catch {
      toast.error("Failed to save feedback");
      refreshWorkspace();
    }
  };

  const active = useMemo(() => admins.find((a) => a.id === selectedId), [admins, selectedId]);

  return (
    <div className="p-6 md:p-10" data-testid="team-page">
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2">Team</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Workspaces</h1>
        <p className="text-stone-400 mt-3">
          Every admin&apos;s recent work at a glance. Click a name on the left to see what they&apos;re building.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* Left sidebar */}
        <aside className="border border-stone-800" data-testid="team-sidebar">
          {loadingList ? (
            <div className="p-6 text-stone-500 text-center">Loading…</div>
          ) : admins.length === 0 ? (
            <div className="p-6 text-stone-500 text-center text-sm">
              No admins found.
            </div>
          ) : (
            <ul>
              {admins.map((a) => {
                const isMe = a.id === user?.id;
                const isSel = a.id === selectedId;
                return (
                  <li key={a.id}>
                    <button
                      onClick={() => setSelectedId(a.id)}
                      className={`w-full text-left px-4 py-3 border-b border-stone-800 last:border-b-0 flex items-center gap-3 transition-colors ${
                        isSel ? "bg-orange-600/10 border-l-2 border-l-orange-600" : "hover:bg-stone-900"
                      }`}
                      data-testid={`team-admin-${a.id}`}
                    >
                      <div className={`w-9 h-9 flex items-center justify-center border font-mono-tc text-sm shrink-0 ${
                        isSel ? "border-orange-600 text-orange-400" : "border-stone-700 text-stone-400"
                      }`}>
                        {initialsFor(a.name || a.email)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-display uppercase font-bold text-sm truncate">
                            {a.name || a.email.split("@")[0]}
                          </span>
                          {isMe && (
                            <span className="text-[9px] uppercase tracking-widest bg-stone-800 text-stone-300 px-1 py-px shrink-0">
                              You
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-stone-500 font-mono-tc flex items-center gap-2">
                          <span>{a.videos_count}v</span>
                          <span>·</span>
                          <span>{a.clips_count}c</span>
                          <span>·</span>
                          <span>{a.reels_count}r</span>
                        </div>
                        <div className="text-[10px] text-stone-600 mt-0.5 flex items-center gap-1">
                          <Clock size={9} /> {relativeTime(a.last_active)}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Right panel */}
        <section data-testid="team-workspace">
          {loadingWs || !workspace ? (
            <div className="border border-stone-800 p-14 text-center text-stone-500">
              <Loader2 size={22} className="animate-spin mx-auto mb-2 text-orange-500" />
              Loading workspace…
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Workspace of</div>
                  <div className="font-display uppercase text-3xl font-bold">{workspace.admin.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5 font-mono-tc">{workspace.admin.email}</div>
                </div>
                <div className="flex gap-2">
                  <StatChip icon={Clock} value={workspace.pending_cuts} label="Pending cuts" tone="orange" />
                  <StatChip icon={Film} value={active?.videos_count} label="Videos" />
                  <StatChip icon={Scissors} value={active?.clips_count} label="Clips" />
                  <StatChip icon={Layers} value={active?.reels_count} label="Reels" />
                </div>
              </div>

              <WorkspaceSection
                title="Recent videos"
                empty="No videos yet"
                items={workspace.videos}
                render={(v) => (
                  <Link
                    to={`/studio/${v.id}`}
                    key={v.id}
                    className="border border-stone-800 hover:border-orange-700 p-3 flex items-center gap-3 transition-colors"
                    data-testid={`ws-video-${v.id}`}
                  >
                    <Film size={16} className="text-stone-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase font-bold truncate text-sm">{v.original_name}</div>
                      <div className="text-[10px] text-stone-500 font-mono-tc">
                        {v.status} · {v.duration ? fmtDuration(v.duration) : "—"} · {relativeTime(v.uploaded_at)}
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-stone-600 shrink-0" />
                  </Link>
                )}
              />

              <WorkspaceSection
                title="Recent clips"
                empty="No clips yet"
                items={workspace.clips}
                render={(c) => (
                  <div
                    key={c.id}
                    className="border border-stone-800 p-3 flex items-center gap-3 group hover:border-stone-700 transition-colors"
                    data-testid={`ws-clip-${c.id}`}
                  >
                    <button
                      onClick={() => setPreviewClip(c)}
                      className="w-9 h-9 shrink-0 border border-stone-700 hover:border-orange-600 hover:text-orange-400 flex items-center justify-center text-stone-400 transition-colors"
                      title="Play preview"
                      data-testid={`ws-clip-play-${c.id}`}
                    >
                      <Play size={13} fill="currentColor" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase font-bold truncate text-sm">
                        {c.rider_horse_name || c.sub_label || "Untitled"}
                      </div>
                      <div className="text-[10px] text-stone-500 font-mono-tc">
                        {fmtDuration(c.duration || 0)} · {relativeTime(c.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleFeedback(c, "good")}
                        className={`p-1.5 transition-colors ${c.feedback === "good" ? "text-emerald-400" : "text-stone-500 hover:text-emerald-400"}`}
                        title="Good cut"
                        data-testid={`ws-clip-thumbs-up-${c.id}`}
                      >
                        <ThumbsUp size={14} fill={c.feedback === "good" ? "currentColor" : "none"} />
                      </button>
                      <button
                        onClick={() => handleFeedback(c, "bad")}
                        className={`p-1.5 transition-colors ${c.feedback === "bad" ? "text-rose-400" : "text-stone-500 hover:text-rose-400"}`}
                        title="Bad cut"
                        data-testid={`ws-clip-thumbs-down-${c.id}`}
                      >
                        <ThumbsDown size={14} fill={c.feedback === "bad" ? "currentColor" : "none"} />
                      </button>
                    </div>
                  </div>
                )}
              />

              <WorkspaceSection
                title="Reels"
                empty="No reels yet"
                items={workspace.reels}
                render={(r) => (
                  <Link
                    to="/reels"
                    key={r.id}
                    className="border border-stone-800 hover:border-orange-700 p-3 flex items-center gap-3 transition-colors"
                    data-testid={`ws-reel-${r.id}`}
                  >
                    <Layers size={16} className="text-emerald-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase font-bold truncate text-sm">{r.name || "Untitled reel"}</div>
                      <div className="text-[10px] text-stone-500 font-mono-tc">
                        {r.clip_count} clips · {r.status} · {relativeTime(r.created_at)}
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-stone-600 shrink-0" />
                  </Link>
                )}
              />
            </>
          )}
        </section>
      </div>

      {previewClip && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewClip(null)}
          data-testid="ws-preview-modal"
        >
          <div className="bg-stone-950 border border-stone-700 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
              <div className="min-w-0">
                <div className="label-tiny">Preview</div>
                <div className="font-display uppercase text-lg truncate">
                  {previewClip.rider_horse_name || previewClip.sub_label || "Clip"}
                </div>
              </div>
              <button onClick={() => setPreviewClip(null)} className="btn-secondary" data-testid="ws-close-preview">
                <X size={14} />
              </button>
            </div>
            <video
              src={`${BACKEND_URL}/api/clips/${previewClip.id}/stream`}
              controls
              autoPlay
              playsInline
              className="w-full bg-black"
              data-testid="ws-preview-video"
            />
            <div className="p-4 flex flex-wrap gap-3 items-center text-xs text-stone-400 font-mono-tc">
              <span>{fmtDuration(previewClip.duration || 0)}</span>
              {previewClip.rider_horse_name && (<><span>·</span><span>{previewClip.rider_horse_name}</span></>)}
              <div className="flex items-center gap-1 ml-auto">
                <button
                  onClick={() => handleFeedback(previewClip, "good")}
                  className={`p-1.5 border border-stone-800 transition-colors ${previewClip.feedback === "good" ? "text-emerald-400 border-emerald-700" : "text-stone-500 hover:text-emerald-400"}`}
                  title="Good cut"
                  data-testid="ws-preview-thumbs-up"
                >
                  <ThumbsUp size={14} fill={previewClip.feedback === "good" ? "currentColor" : "none"} />
                </button>
                <button
                  onClick={() => handleFeedback(previewClip, "bad")}
                  className={`p-1.5 border border-stone-800 transition-colors ${previewClip.feedback === "bad" ? "text-rose-400 border-rose-700" : "text-stone-500 hover:text-rose-400"}`}
                  title="Bad cut"
                  data-testid="ws-preview-thumbs-down"
                >
                  <ThumbsDown size={14} fill={previewClip.feedback === "bad" ? "currentColor" : "none"} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ icon: Icon, value, label, tone }) {
  const border = tone === "orange" ? "border-orange-700" : "border-stone-800";
  const color = tone === "orange" ? "text-orange-400" : "text-stone-300";
  return (
    <div className={`border ${border} px-3 py-1.5 flex items-center gap-2`}>
      <Icon size={12} className={color} />
      <div>
        <div className={`font-mono-tc text-sm ${color}`}>{value ?? 0}</div>
        <div className="text-[9px] uppercase tracking-widest text-stone-500 -mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function WorkspaceSection({ title, empty, items, render }) {
  return (
    <div className="mb-8" data-testid={`ws-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between mb-3 border-b border-stone-800 pb-2">
        <h2 className="font-display uppercase text-xl font-bold tracking-tight">{title}</h2>
        <span className="font-mono-tc text-xs text-stone-500">
          <span className="text-orange-500 text-base">{items.length}</span>
        </span>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-6 text-center text-stone-500 text-sm">
          {empty}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {items.map(render)}
        </div>
      )}
    </div>
  );
}
